import { Plus, Store, Trophy } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'

import { useAuth } from '@/features/auth/hooks/useAuth'
import { NotificationBell } from '@/features/notifications/components/NotificationBell'
import { CreateSessionLink } from '@/features/sessions/components/CreateSessionLink'
import { useMyVenuesQuery } from '@/features/stores/hooks/useStore'
import { useChangeLocale } from '@/shared/hooks/useChangeLocale'
import { useLocalizedHref } from '@/shared/hooks/useLocalizedHref'
import { cn } from '@/shared/lib/cn'
import { type Locale } from '@/shared/store/localeStore'

import { AdminMenu } from './AdminMenu'
import { UserMenu } from './UserMenu'

const LOCALES: { code: Locale; label: string }[] = [
  { code: 'es', label: 'ES' },
  { code: 'en', label: 'EN' },
]

/**
 * SiteHeader brutal — header global de la app.
 *
 * Layout desktop (md+):
 *   - Container brutal `rounded-2xl` centrado dentro de `max-w-7xl`.
 *   - Logo izquierda · Nav center · CTAs/UserMenu derecha.
 *   - Sticky top: se mantiene visible al scrollear (banda translúcida para
 *     que el contenido no se cuele por el gutter alrededor de la card).
 *
 * Layout mobile (<md):
 *   - Sticky top con backdrop-blur.
 *   - Logo + toggle compacto ES/EN.
 *   - No hay trigger de avatar: las acciones de cuenta viven en el tab
 *     "Cuenta" del MobileTabBar (que abre `AccountMenu`).
 */
export function SiteHeader() {
  const { t } = useTranslation()
  const { isAuthenticated, user } = useAuth()
  const location = useLocation()
  const helpHref = useLocalizedHref('help-hub')

  const isSessionsActive = location.pathname === '/sessions'
  const isVenuesActive = location.pathname.startsWith('/locales')
  const isMyActive = location.pathname.startsWith('/sessions/mine')
  // Una cuenta TIENDA no juega — nunca ve "Mis partidas"/CTA de crear
  // (bloqueado 403 en backend), en su lugar ve sus enlaces de tienda.
  const isStoreAccount = user?.role === 'TIENDA'
  const isStoreActive = location.pathname.startsWith('/tienda')
  // CTA primaria contextual de la tienda: sin locales aún → "Añadir local"
  // (onboarding); con locales → "Crear evento" (acción recurrente). La query está
  // gated a `role === TIENDA` y comparte cache con el dashboard (sin fetch extra).
  const venuesQuery = useMyVenuesQuery()
  const storeHasVenues = (venuesQuery.data?.length ?? 0) > 0

  return (
    <>
      {/* Desktop header — container brutal centrado, sticky top */}
      <header className="sticky top-0 z-30 hidden bg-background/85 px-6 pb-4 pt-[calc(1.5rem+env(safe-area-inset-top))] backdrop-blur md:block">
        <div className="brutal mx-auto flex max-w-7xl items-center justify-between rounded-2xl bg-background px-6 py-3">
          <Link to="/" className="flex items-center" aria-label={t('nav.homeAria')}>
            <img
              src="/brand/logo-lockup.png"
              alt=""
              aria-hidden="true"
              width={1500}
              height={478}
              draggable={false}
              className="h-10 w-auto dark:hidden"
            />
            <img
              src="/brand/logo-lockup-light.png"
              alt=""
              aria-hidden="true"
              width={1500}
              height={478}
              draggable={false}
              className="hidden h-10 w-auto dark:block"
            />
          </Link>

          <nav
            className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider"
            aria-label={t('nav.mainAria')}
          >
            <Link
              to="/sessions"
              aria-current={isSessionsActive ? 'page' : undefined}
              onClick={(e) => {
                // Click en "Partidas" estando ya en /sessions: no-op para
                // no pisar los filtros actuales (la URL es la state del
                // listado y un PUSH a /sessions sin query los borraría).
                if (isSessionsActive) e.preventDefault()
              }}
              className={cn(
                'inline-flex items-center rounded-md px-3 py-2 transition-colors hover:bg-yellow/30 hover:text-red',
                isSessionsActive && 'text-red',
              )}
            >
              {t('nav.sessions')}
            </Link>
            <Link
              to="/locales"
              aria-current={isVenuesActive ? 'page' : undefined}
              className={cn(
                'inline-flex items-center rounded-md px-3 py-2 transition-colors hover:bg-yellow/30 hover:text-red',
                isVenuesActive && 'text-red',
              )}
            >
              {t('nav.venues')}
            </Link>
            {isAuthenticated && !isStoreAccount && (
              <Link
                to="/sessions/mine"
                aria-current={isMyActive ? 'page' : undefined}
                className={cn(
                  'inline-flex items-center rounded-md px-3 py-2 transition-colors hover:bg-yellow/30 hover:text-red',
                  isMyActive && 'text-red',
                )}
              >
                {t('nav.mySessions')}
              </Link>
            )}
            {isStoreAccount && (
              <Link
                to="/tienda"
                aria-current={isStoreActive ? 'page' : undefined}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-md px-3 py-2 transition-colors hover:bg-yellow/30 hover:text-red',
                  isStoreActive && 'text-red',
                )}
              >
                <Store size={14} strokeWidth={2.5} aria-hidden="true" />
                {t('nav.myStore')}
              </Link>
            )}
            <Link
              to={helpHref}
              className="inline-flex items-center rounded-md px-3 py-2 transition-colors hover:bg-yellow/30 hover:text-red"
            >
              {t('nav.help')}
            </Link>
            <AdminMenu />
          </nav>

          <div className="flex items-center gap-2">
            {isAuthenticated && isStoreAccount && (
              <Link
                to={storeHasVenues ? '/tienda/eventos' : '/tienda/nuevo-local'}
                className="brutal-sm md:brutal-hover inline-flex items-center gap-1.5 rounded-md bg-red px-4 py-2 text-sm font-bold text-background"
              >
                {storeHasVenues ? (
                  <Trophy size={16} strokeWidth={2.5} aria-hidden="true" />
                ) : (
                  <Plus size={16} strokeWidth={2.5} aria-hidden="true" />
                )}
                {storeHasVenues
                  ? t('stores.events.createNew')
                  : t('stores.home.dashboard.actions.addVenue')}
              </Link>
            )}
            {isAuthenticated && !isStoreAccount && (
              <CreateSessionLink className="brutal-sm md:brutal-hover inline-flex items-center gap-1.5 rounded-md bg-red px-4 py-2 text-sm font-bold text-background">
                <Plus size={16} strokeWidth={2.5} aria-hidden="true" />
                {t('sessions.create.title')}
              </CreateSessionLink>
            )}
            {isAuthenticated && <NotificationBell />}
            {isAuthenticated ? (
              <UserMenu />
            ) : (
              <>
                <LanguageToggleCompact />
                <Link
                  to="/login"
                  className="rounded-md px-4 py-2 text-sm font-bold transition-colors hover:bg-yellow/40"
                >
                  {t('nav.login')}
                </Link>
                <Link
                  to="/register"
                  className="brutal-sm md:brutal-hover rounded-md bg-red px-4 py-2 text-sm font-bold text-background"
                >
                  {t('nav.register')}
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Mobile header — sticky top. El pt reserva la barra de estado del
          sistema (Android 15 edge-to-edge / notch iOS) con env(safe-area-inset-top);
          en pantallas sin inset (web, Android no edge-to-edge) env()=0 → pt-4 normal. */}
      <header className="sticky top-0 z-30 border-b-2 border-foreground/10 bg-background/95 px-4 pb-3 pt-[calc(1rem+env(safe-area-inset-top))] backdrop-blur md:hidden">
        <div className="flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center" aria-label={t('nav.homeAria')}>
            <img
              src="/brand/logo-lockup.png"
              alt=""
              aria-hidden="true"
              width={1500}
              height={478}
              draggable={false}
              className="h-9 w-auto dark:hidden"
            />
            <img
              src="/brand/logo-lockup-light.png"
              alt=""
              aria-hidden="true"
              width={1500}
              height={478}
              draggable={false}
              className="hidden h-9 w-auto dark:block"
            />
          </Link>

          <div className="flex items-center gap-2">
            {isAuthenticated && <NotificationBell />}
            <LanguageToggleCompact />
          </div>
        </div>
      </header>
    </>
  )
}

// ---------------------------------------------------------------------------
// LanguageToggleCompact · pills ES/EN brutal en el header mobile
// ---------------------------------------------------------------------------

function LanguageToggleCompact() {
  const { t } = useTranslation()
  const { locale, changeLocale } = useChangeLocale()

  return (
    <div
      role="group"
      aria-label={t('nav.changeLanguage')}
      className="brutal-sm inline-flex gap-0.5 rounded-md bg-background p-0.5"
    >
      {LOCALES.map(({ code, label }) => (
        <button
          key={code}
          type="button"
          aria-pressed={locale === code}
          onClick={() => changeLocale(code)}
          className={cn(
            'rounded-sm px-2.5 py-1 font-brutal text-[10px] font-bold uppercase tracking-wider transition-colors',
            locale === code
              ? 'bg-foreground text-background'
              : 'text-muted-foreground hover:bg-yellow/40',
          )}
        >
          {label}
        </button>
      ))}
    </div>
  )
}
